import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesApi } from "../services/api";

function CorporateTrainingDetailPage() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProgram();
  }, [id]);

  const fetchProgram = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesApi.getCorporateTrainingProgram(id);
      setProgram(response.data);

      // Fetch benefits
      const benefitsRes = await servicesApi.getCorporateTrainingBenefits();
      setBenefits(benefitsRes.data || []);
    } catch (err) {
      console.error("Error loading program:", err);
      setError("Program not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">
            Program Not Found
          </h1>
          <p className="text-red-600 mb-6">
            {error ||
              "The training program you are looking for does not exist."}
          </p>
          <Link
            to="/corporate-training"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Back to Corporate Training
          </Link>
        </div>
      </div>
    );
  }

  const topics = program.topics
    ? program.topics.split(",").map((t) => t.trim())
    : [];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link
              to="/corporate-training"
              className="text-blue-100 hover:text-white transition"
            >
              ← Back to Corporate Training
            </Link>
          </div>
          <div className="flex items-start gap-6 mb-6">
            {program.icon && <div className="text-6xl">{program.icon}</div>}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {program.title}
              </h1>
              <p className="text-blue-100 text-xl">{program.description}</p>
            </div>
          </div>
          {program.duration && (
            <div className="bg-blue-500 bg-opacity-20 px-4 py-2 rounded-lg inline-block">
              <div className="text-sm text-blue-100">Duration</div>
              <div className="font-semibold">{program.duration}</div>
            </div>
          )}
        </div>
      </section>

      {/* Topics */}
      {topics.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Key Topics Covered
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {topics.map((topic, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow p-6 flex items-start gap-4"
                >
                  <div className="text-blue-600 text-2xl mt-1">📌</div>
                  <div>
                    <p className="text-gray-700 font-semibold">{topic}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Program Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-blue-50 rounded-lg p-8 border border-blue-200"
                >
                  <div className="text-3xl mb-4">{benefit.icon || "✓"}</div>
                  <h3 className="text-xl font-bold mb-3 text-blue-800">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Transform Your Team's Skills
          </h2>
          <p className="mb-8 text-blue-100 text-lg">
            Invest in your team's development and drive organizational growth.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Inquire About This Program
          </Link>
        </div>
      </section>

      {/* Back to Corporate Training */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/corporate-training"
            className="text-blue-600 font-semibold hover:text-blue-700 transition"
          >
            ← View All Training Programs
          </Link>
        </div>
      </section>
    </div>
  );
}

export default CorporateTrainingDetailPage;
